import React from "react";
import { useNavigate } from "react-router-dom";
import { Store, User, Phone, CalendarClock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/common/StatusBadge";
import { statusMeta } from "@/lib/constants";
import { formatINRShort, formatDateShort, todayISO } from "@/lib/format";

function followUpTone(dateStr) {
  if (!dateStr) return null;
  const today = todayISO();
  if (dateStr < today) return { label: "Overdue", cls: "bg-destructive/10 text-destructive" };
  if (dateStr === today) return { label: "Due today", cls: "bg-warning/10 text-warning" };
  return { label: `Follow-up ${formatDateShort(dateStr)}`, cls: "bg-accent text-accent-foreground" };
}

export const LeadCard = ({ lead, showOwner = false }) => {
  const navigate = useNavigate();
  const meta = statusMeta(lead.status);
  const title =
    [lead.make, lead.model].filter(Boolean).join(" ") ||
    lead.vehicle_number ||
    lead.customer_name ||
    lead.lead_no;
  const isClosed = lead.status === "DEAL_DONE" || lead.status === "LOST";
  const fu = !isClosed ? followUpTone(lead.next_follow_up) : null;
  const who = lead.lead_type === "dealership" ? lead.dealership_name : lead.customer_name;

  return (
    <button
      onClick={() => navigate(`/leads/${lead.id}`)}
      className="group relative flex w-full items-stretch gap-3 overflow-hidden rounded-xl border border-border bg-card p-3.5 text-left shadow-card transition-shadow hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", meta.bar)} />
      <div className="min-w-0 flex-1 pl-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] font-semibold text-muted-foreground">{lead.lead_no}</span>
          <StatusBadge status={lead.status} size="sm" />
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground">
            {lead.lead_type === "dealership" ? <Store className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {lead.lead_type === "dealership" ? "Dealer" : "Individual"}
          </span>
        </div>

        <div className="mt-1.5 flex items-baseline gap-2">
          <h3 className="truncate text-[15px] font-bold text-foreground">{title}</h3>
          {lead.year && <span className="text-xs text-muted-foreground">{lead.year}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {lead.vehicle_number && <span className="font-medium text-foreground/70">{lead.vehicle_number}</span>}
          {lead.fuel_type && <span>· {lead.fuel_type}</span>}
          {who && <span className="inline-flex items-center gap-1">· {who}</span>}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          {(lead.demand || lead.offer_price) && (
            <div className="flex items-center gap-2 text-xs">
              {lead.demand != null && (
                <span className="text-muted-foreground">Demand <b className="tnum text-foreground">{formatINRShort(lead.demand)}</b></span>
              )}
              {lead.offer_price != null && (
                <span className="text-muted-foreground">Offer <b className="tnum text-brand">{formatINRShort(lead.offer_price)}</b></span>
              )}
            </div>
          )}
          {fu && (
            <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold", fu.cls)}>
              <CalendarClock className="h-3 w-3" /> {fu.label}
            </span>
          )}
          {showOwner && lead.owner_name && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <User className="h-3 w-3" /> {lead.owner_name}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="my-auto h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
};
